import React, { useState } from "react";
import { ArrowRight, Check } from "lucide-react";
import { submitTeamsInterest } from "../utils/api";
import {
  Button,
  Container,
  Section,
  Eyebrow,
  Display,
  Hairline,
  Stat,
  SEO,
} from "./ui";
import { canonicalFor } from "../routes";

const FEATURES = [
  { title: "Centralized branding.",         body: "Upload your logo, set brand colors, define approved link types. Every team member's card renders on-brand without a designer's involvement." },
  { title: "Admin dashboard.",              body: "Add, edit, and remove team members from one console. See who is active, whose card is unfilled, who has not used theirs in 90 days." },
  { title: "Instant onboard and offboard.", body: "New hire? Card live in seconds. Someone leaving? Revoke access immediately, retain analytics, archive their card for the records." },
  { title: "Enforce card content.",         body: "Lock required fields and approved link types so every card meets company standards." },
  { title: "Bulk physical card ordering.",  body: "Order NFC cards for the whole team in one batch. Volume pricing starts at three cards." },
  { title: "Aggregated analytics.",         body: "Visit counts, click rates, and engagement across the team in one view. Per-rep, per-card, per-event." },
];

const VOLUME_TIERS = [
  { min: "3+",  price: "$13.49 each" },
  { min: "5+",  price: "$12.74 each" },
  { min: "10+", price: "$11.99 each" },
  { min: "25+", price: "$11.69 each" },
  { min: "50+", price: "$11.24 each" },
];

const TEAM_SIZE_OPTIONS = ["5–10", "10–25", "25–50", "50–100", "100+"];

export default function Teams() {
  const [companyName, setCompanyName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitTeamsInterest({ companyName, adminEmail, teamSize, message });
    } catch {
      /* still acknowledge */
    }
    setSending(false);
    setSubmitted(true);
    setCompanyName("");
    setAdminEmail("");
    setTeamSize("");
    setMessage("");
  };

  return (
    <>
      <SEO
        title="WhoBrew for Teams — NFC business cards for an entire team"
        description="Cards for an entire team with centralized branding, admin controls, and aggregated analytics. $3.99 per person per month, no annual contract."
        canonical={canonicalFor("/teams")}
      />

      {/* Hero */}
      <Section tone="page" spacing="loose" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
            <div className="lg:col-span-7">
              <Eyebrow>For teams</Eyebrow>
              <Display as="h1" scale="lg" italicWord="brand" className="mt-5">
                One brand. Every card.
              </Display>
              <p className="mt-7 text-lg text-coffee-700 dark:text-cream-200 dark:text-cream-200 max-w-xl leading-relaxed">
                Cards for an entire team with centralized branding, admin controls, bulk physical
                card ordering, and aggregated analytics across every rep, recruiter, or agent.
              </p>
            </div>
            <div className="lg:col-span-5">
              <Stat value="$3.99" caption="per person per month, no annual contract." />
            </div>
          </div>
        </Container>
      </Section>

      {/* Features */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-latte-300 dark:bg-latte-800 border border-latte-300 dark:border-latte-800 dark:border-latte-800 rounded-lg overflow-hidden">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-cream-50 dark:bg-coffee-900 dark:bg-coffee-900 p-8">
                <Eyebrow step={i + 1} of={FEATURES.length}>Feature</Eyebrow>
                <Display as="h3" scale="sm" className="mt-3 text-2xl">{f.title}</Display>
                <p className="mt-4 text-body-sm text-coffee-700 dark:text-cream-200 dark:text-cream-200 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Bulk pricing */}
      <Section tone="raised" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Bulk physical cards</Eyebrow>
          <Display as="h2" scale="md" italicWord="bulk" className="mt-4">
            Order NFC cards in bulk.
          </Display>
          <p className="mt-5 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200">
            Each card can carry its own name and title. Volume pricing kicks in at three cards.
          </p>
          <Hairline className="my-8" />
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-6">
            {VOLUME_TIERS.map((t) => (
              <div key={t.min}>
                <p className="font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 dark:text-cream-200">{t.min}</p>
                <p className="mt-2 font-display text-headline text-coffee-900 dark:text-cream-50 dark:text-cream-50">{t.price}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Button to="/order-card" size="lg">Start a batch order</Button>
          </div>
        </Container>
      </Section>

      {/* Interest form */}
      <Section tone="page" spacing="default" hairlineBottom>
        <Container width="prose">
          <Eyebrow>Talk to us</Eyebrow>
          <Display as="h2" scale="md" italicWord="team" className="mt-4 mb-8">
            Tell us about your team.
          </Display>
          {submitted ? (
            <div className="border border-latte-300 dark:border-latte-800 dark:border-latte-800 rounded-lg p-10 bg-cream-100 dark:bg-coffee-800 dark:bg-coffee-800">
              <Check className="w-8 h-8 text-success-700 mb-4" strokeWidth={2.5} />
              <Display as="h3" scale="sm">Thanks.</Display>
              <p className="mt-4 text-body text-coffee-700 dark:text-cream-200 dark:text-cream-200">
                We'll be in touch within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <FormField id="companyName" label="Company name">
                <input id="companyName" type="text" required value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="teams-input" placeholder="Acme Coffee Co." />
              </FormField>
              <FormField id="adminEmail" label="Admin email">
                <input id="adminEmail" type="email" required value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} className="teams-input" placeholder="admin@company.com" />
              </FormField>
              <FormField id="teamSize" label="Team size">
                <select id="teamSize" required value={teamSize} onChange={(e) => setTeamSize(e.target.value)} className="teams-input">
                  <option value="" disabled>Select team size</option>
                  {TEAM_SIZE_OPTIONS.map((o) => (
                    <option key={o} value={o}>{o}</option>
                  ))}
                </select>
              </FormField>
              <FormField id="message" label="Anything else (optional)">
                <textarea id="message" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="teams-input resize-vertical" placeholder="Tell us about your team's setup, your event calendar, or what you've tried before." />
              </FormField>
              <Button type="submit" size="lg" loading={sending}>
                {sending ? "Submitting" : "Get started"}
              </Button>
              <style>{`
                .teams-input {
                  width: 100%;
                  padding: 0.75rem 1rem;
                  background: transparent;
                  border: 1px solid #c9b89e;
                  border-radius: 4px;
                  color: inherit;
                  font-family: inherit;
                  font-size: 1rem;
                  outline: none;
                  transition: border-color 150ms;
                }
                .teams-input:focus { border-color: #1f1410; }
                .dark .teams-input { border-color: #443a30; }
                .dark .teams-input:focus { border-color: #fbf7f1; }
              `}</style>
            </form>
          )}
        </Container>
      </Section>

      {/* CTA */}
      <Section tone="ink" spacing="default">
        <Container className="text-center">
          <Display as="h2" scale="lg" italicWord="brand" className="mx-auto max-w-3xl">
            One brand. Every card.
          </Display>
          <div className="mt-10 flex justify-center">
            <Button to="/contact" size="lg" onInk trailingIcon={<ArrowRight className="w-4 h-4" />}>
              Talk to us
            </Button>
          </div>
        </Container>
      </Section>
    </>
  );
}

function FormField({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className="block font-mono text-eyebrow uppercase text-coffee-600 dark:text-cream-200 dark:text-cream-200 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}
